import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Layers,
  Award,
  Brain,
  Calendar,
  FileText,
  Download,
  Eye,
  UserPlus2,
  UserMinus2,
} from "lucide-react";
import { normalizeMediaUrl } from "utils/getImageUrl";
import { getMediaUrl } from "utils/getMediaUrl";
import { LEVEL_OPTIONS } from "list";
import { expertImages } from "dataClient/expert";
import { countriesFR } from "data/countriesFR";
import CardActionButtons from "components/CardActionButtons";
import DangerIndicator from "components/DangerIndicator";
import HighlightText from "components/HighlightText";
import axios from "utils/axios";
import { openSnackbar } from "api/snackbar";
import { brand } from "brand/theme";
import formationEmptyIcon from "assets/icons/playlist-01_w.svg";
import formationBadgeIcon from "assets/icons/playlist-01.svg";
import moduleEmptyIcon from "assets/icons/play-list_w.svg";
import podcastEmptyIcon from "assets/icons/co-present.svg";
import eventEmptyIcon from "assets/icons/calendar-03.svg";
import libraryEmptyIcon from "assets/icons/document-attachment.svg";
import institutionEmptyIcon from "assets/icons/court-house.svg";
import medicationEmptyIcon from "assets/icons/medicine-02.svg";
import pathologyEmptyIcon from "assets/icons/diseases.svg";
import cnssLogo from "assets/images/institutions/cnss.png";
import { getInstitutionIconUrl } from "utils/institutionSelect";

function EmptyMediaIcon({ src }) {
  return (
    <div className="f2-card-media-icon" aria-hidden="true">
      <img src={src} alt="" style={{ filter: brand.iconFilters.accent }} />
    </div>
  );
}

function levelLabel(level) {
  if (!level) return null;
  const match = LEVEL_OPTIONS.find(
    (opt) => opt.id === level || opt.name === level,
  );
  return match?.name || level;
}

function organiserName(item) {
  const org = item?.organisateur;
  if (!org) return "";
  if (typeof org === "string") return org;
  if (Array.isArray(org) && org.length) {
    const first = org[0];
    if (typeof first === "string") return first;
    return first?.name || first?.title || "";
  }
  return org?.name || org?.title || "";
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

const EVENT_NATURE_LABELS = {
  FREE: "Gratuit",
  PAID: "Payant",
  INVITATION: "Invitation",
};

const EVENT_PARTICIPATION_LABELS = {
  ONSITE: "Présentiel",
  ONLINE: "Digitale",
  HYBRID: "Phygital",
};

function eventNatureLabel(nature) {
  if (!nature) return "";
  const raw = String(nature);
  return EVENT_NATURE_LABELS[raw] || EVENT_NATURE_LABELS[raw.toUpperCase()] || raw;
}

function eventParticipationLabel(participation) {
  if (!participation) return "";
  const raw = String(participation);
  const upper = raw.toUpperCase();
  if (EVENT_PARTICIPATION_LABELS[upper]) return EVENT_PARTICIPATION_LABELS[upper];
  const lower = raw.toLowerCase();
  const byFrench = {
    présentiel: "Présentiel",
    presentiel: "Présentiel",
    digitale: "Digitale",
    online: "Digitale",
    phygital: "Phygital",
    hybrid: "Phygital",
  };
  return byFrench[lower] || raw;
}

function formatEventDateTime(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
    if (timePart && timePart !== "00:00") {
      return `${datePart} · ${timePart}`;
    }
    return datePart;
  } catch {
    return value;
  }
}

function institutionIconSrc(institution) {
  return getInstitutionIconUrl(institution, getMediaUrl);
}

export function CardInstitutionRelations({
  institutions = [],
  highlightQuery = "",
  ariaLabel = "Institutions associées",
  max = 2,
}) {
  const all = (institutions || []).filter(Boolean);
  const list = all.slice(0, max);
  const extra = all.length - list.length;
  if (!list.length) return null;

  const single = list.length === 1 ? list[0] : null;
  const singleIcon = single ? institutionIconSrc(single) : null;

  if (single) {
    return (
      <div className="f2-card-institution-named" aria-label={ariaLabel}>
        <span className={`f2-card-institution-logo${singleIcon ? "" : " is-empty"}`}>
          {singleIcon ? (
            <img src={singleIcon} alt="" />
          ) : (
            <img
              src={institutionEmptyIcon}
              alt=""
              className="f2-card-institution-fallback"
              style={{ filter: brand.iconFilters.accent }}
            />
          )}
        </span>
        {single?.name ? (
          <span className="f2-card-institution-name">
            <HighlightText text={single.name} query={highlightQuery} />
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="f2-card-institutions" aria-label={ariaLabel}>
      {list.map((institution) => {
        const iconUrl = institutionIconSrc(institution);
        const tooltip = institution?.name || "Institution";
        return (
          <span
            key={institution.id || institution.name}
            className="f2-card-institution"
            data-tooltip={tooltip}
            aria-label={tooltip}
          >
            {iconUrl ? (
              <img src={iconUrl} alt="" />
            ) : (
              <img
                src={institutionEmptyIcon}
                alt=""
                className="f2-card-institution-fallback"
                style={{ filter: brand.iconFilters.accent }}
              />
            )}
          </span>
        );
      })}
      {extra > 0 ? (
        <span
          className="f2-card-institution-more"
          data-tooltip={all
            .slice(max)
            .map((institution) => institution?.name)
            .filter(Boolean)
            .join(" · ")}
          aria-label={`${extra} autre${extra > 1 ? "s" : ""} institution${extra > 1 ? "s" : ""}`}
        >
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

function expertAvatarSrc(person) {
  const raw =
    person?.profile_image || person?.avatar || person?.picture || null;
  if (!raw) return null;
  if (typeof raw === "string" && raw.startsWith("http")) return raw;
  return normalizeMediaUrl(raw);
}

function expertInitials(person) {
  const name =
    person?.name ||
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim() ||
    "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function expertTitlePrefix(title) {
  if (title === "DOC") return "Dr";
  if (title === "PROF") return "Pr";
  return "";
}

function expertFullName(person) {
  return (
    person?.full_name ||
    person?.name ||
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim() ||
    "Expert"
  );
}

function expertProfessionLabel(person) {
  if (!person?.profession) return "";
  if (typeof person.profession === "string") return person.profession;
  return person.profession?.name || "";
}

function expertTooltip(person) {
  const name = expertFullName(person);
  const prefix = expertTitlePrefix(person?.title);
  const profession = expertProfessionLabel(person);
  const displayName = prefix ? `${prefix} ${name}` : name;
  return [displayName, profession].filter(Boolean).join(" · ");
}

export function ExpertAvatars({
  people = [],
  emptyLabel = "—",
  max = 4,
  withDivider = false,
  showSingleName = true,
}) {
  const list = (people || []).filter(Boolean);
  if (!list.length) {
    if (withDivider) return null;
    return <p className="f2-card-org">{emptyLabel}</p>;
  }
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  const single = shown.length === 1 ? shown[0] : null;
  const singleName = single ? expertFullName(single) : null;

  const renderAvatar = (person, key) => {
    const src = expertAvatarSrc(person);
    const tooltip = expertTooltip(person);
    return (
      <span
        key={key}
        className="f2-card-avatar-wrap"
        data-tooltip={tooltip}
        aria-label={tooltip}
      >
        {src ? (
          <img className="f2-card-avatar" src={src} alt="" />
        ) : (
          <span className="f2-card-avatar f2-card-avatar-fallback">
            {expertInitials(person)}
          </span>
        )}
      </span>
    );
  };

  const avatars = single && showSingleName ? (
    <div className="f2-card-expert-named" aria-label="Expert associé">
      {renderAvatar(single, single.id || singleName)}
      <span className="f2-card-expert-name">{singleName}</span>
    </div>
  ) : (
    <div className="f2-card-avatars" aria-label="Experts">
      {shown.map((person, index) =>
        renderAvatar(person, person.id || person.name || index),
      )}
      {extra > 0 && <span className="f2-card-avatar-more">+{extra}</span>}
    </div>
  );

  if (!withDivider) return avatars;

  return (
    <>
      <div className="f2-card-divider" role="separator" />
      <div className="f2-card-relations">{avatars}</div>
    </>
  );
}

const cardMotion = {
  layout: true,
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
};

/** Formation listing card (/formation, favorites, apprentissage, related). */
export function FormationV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  featured = false,
  highlightQuery = "",
  emptyIconSrc = formationEmptyIcon,
}) {
  const category =
    item?.category?.name || item?.Categorie?.name || "Formation";
  const level = levelLabel(item?.level);
  const thumb = item?.thumbnails
    ? normalizeMediaUrl(item.thumbnails)
    : null;
  const modulesCount =
    item?.modules?.modules_count ||
    item?.modules?.allModules?.length ||
    item?.modules_count ||
    0;
  const hasCert = Array.isArray(item?.certificat)
    ? item.certificat.length > 0
    : Boolean(item?.certificat);
  const org = organiserName(item);
  const isFavorite = Boolean(
    item?.is_bookmarked ?? item?.IsFavorite ?? item?.is_favorite,
  );

  return (
    <motion.article
      {...cardMotion}
      className={`f2-card${featured ? " f2-card-featured" : ""}`}
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? <img src={thumb} alt="" /> : <EmptyMediaIcon src={emptyIconSrc} />}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          <span className="f2-chip f2-chip-light">
            <HighlightText text={category} query={highlightQuery} />
          </span>
          {hasCert && (
            <span className="f2-chip f2-chip-light" title="Certifiante">
              <Award size={12} />
            </span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="formation"
            editType="formation"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {modulesCount > 0 && (
            <span className="f2-chip f2-chip-dark">
              <Layers size={12} />
              {modulesCount} modules
            </span>
          )}
        </div>
      </div>

      <div className="f2-card-body">
        <div className="f2-card-tags">
          {level && (
            <span className="f2-tag">
              <HighlightText text={level} query={highlightQuery} />
            </span>
          )}
        </div>
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        <p className="f2-card-org">
          <HighlightText
            text={org || "Organisateur"}
            query={highlightQuery}
          />
        </p>
      </div>
    </motion.article>
  );
}

/** Masterclass / podcast listing card. */
export function MasterclassV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  featured = false,
  highlightQuery = "",
}) {
  return (
    <FormationV2Card
      item={item}
      onOpen={onOpen}
      onFavoriteChange={onFavoriteChange}
      isOnFavoritesPage={isOnFavoritesPage}
      featured={featured}
      highlightQuery={highlightQuery}
      emptyIconSrc={podcastEmptyIcon}
    />
  );
}

/** Module listing card. */
export function ModuleV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const level = levelLabel(item?.level || item?.formation?.level);
  const thumb = item?.thumbnails
    ? normalizeMediaUrl(item.thumbnails)
    : null;
  const formationTitle =
    item?.formation?.title || item?.formation?.name || "Formation";
  const hasQuiz = Boolean(item?.quizzes?.id || item?.quiz);
  const order = item?.order;
  const parentId = item?.formation_id || item?.formation?.id;
  const isFavorite = Boolean(
    item?.is_bookmarked ?? item?.IsFavorite ?? item?.is_favorite,
  );

  const handleOpen = () => {
    if (onOpen) onOpen(item);
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card m2-card"
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleOpen();
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? <img src={thumb} alt="" /> : <EmptyMediaIcon src={moduleEmptyIcon} />}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          {order != null && (
            <span className="f2-chip f2-chip-light">N° {order}</span>
          )}
          {hasQuiz && (
            <span className="f2-chip f2-chip-light" title="Quiz">
              <Brain size={12} />
              Quiz
            </span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="module"
            editType="module"
            parentId={parentId}
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {level && <span className="f2-chip f2-chip-dark">{level}</span>}
          {item?.parentType === "masterclass" && (
            <span className="f2-chip f2-chip-dark">Masterclass</span>
          )}
        </div>
      </div>

      <div className="f2-card-body">
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        <p className="f2-card-org">
          <HighlightText text={formationTitle} query={highlightQuery} />
        </p>
      </div>
    </motion.article>
  );
}

/** Event listing card (/agenda, favorites, related). */
export function EventV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const typeName = item?.type?.name || "Événement";
  const thumb = item?.image ? normalizeMediaUrl(item.image) : null;
  const people =
    item?.intervenants?.length > 0
      ? item.intervenants
      : item?.experts?.length > 0
        ? item.experts
        : item?.participants || [];
  const organisators = Array.isArray(item?.organisators) ? item.organisators : [];
  const participationLabel = eventParticipationLabel(item?.participation);
  const natureLabel = eventNatureLabel(item?.event_nature || item?.nature);
  const eventDate = item?.start_date || item?.end_date;
  const isFavorite = Boolean(item?.IsFavorite ?? item?.is_favorite);
  const showRelations = organisators.length > 0 || people.length > 0;
  const showMediaBottom = participationLabel || natureLabel;

  return (
    <motion.article
      {...cardMotion}
      className="f2-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? <img src={thumb} alt="" /> : <EmptyMediaIcon src={eventEmptyIcon} />}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          <div className="f2-card-media-tags">
            <span className="f2-chip f2-chip-light">
              <HighlightText text={typeName} query={highlightQuery} />
            </span>
          </div>
          <CardActionButtons
            id={item.id}
            favoriteType="event"
            editType="event"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        {showMediaBottom ? (
          <div className="f2-card-media-bottom f2-card-media-bottom-split">
            {participationLabel ? (
              <span className="f2-chip f2-chip-dark">{participationLabel}</span>
            ) : (
              <span aria-hidden="true" />
            )}
            {natureLabel ? (
              <span className="f2-chip f2-chip-dark">{natureLabel}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="f2-card-body">
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        {(eventDate || showRelations) && (
          <div className="f2-card-event-meta">
            {eventDate ? (
              <p className="f2-card-date f2-card-event-date">
                <Calendar size={14} strokeWidth={2} />
                {formatEventDateTime(eventDate)}
              </p>
            ) : null}
            {showRelations ? (
              <>
                <div className="f2-card-divider f2-card-event-divider" role="separator" />
                <div className="f2-card-relations f2-card-event-relations">
                  <CardInstitutionRelations
                    institutions={organisators}
                    highlightQuery={highlightQuery}
                    ariaLabel="Organisé par"
                    max={2}
                  />
                  {people.length > 0 ? (
                    <ExpertAvatars people={people} showSingleName={false} />
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </motion.article>
  );
}

/** Library document listing card. */
export function LibraryV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category = item?.category?.name || "Document";
  const thumb = item?.thumbnail ? normalizeMediaUrl(item.thumbnail) : null;
  const fileType = item?.file_type
    ? String(item.file_type).toUpperCase()
    : null;
  const isFavorite = Boolean(item?.is_favorite ?? item?.IsFavorite);

  return (
    <motion.article
      {...cardMotion}
      className="f2-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? <img src={thumb} alt="" /> : <EmptyMediaIcon src={libraryEmptyIcon} />}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          <div className="f2-card-media-tags">
            <span className="f2-chip f2-chip-light">
              <HighlightText text={category} query={highlightQuery} />
            </span>
            {fileType && (
              <span className="f2-chip f2-chip-light">{fileType}</span>
            )}
          </div>
          <CardActionButtons
            id={item.id}
            favoriteType="library"
            editType="library"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>
      </div>

      <div className="f2-card-body">
        <h3 className="f2-card-title">
          <HighlightText
            text={item?.name || item?.title}
            query={highlightQuery}
          />
        </h3>
        <div className="f2-card-lib-meta">
          {item?.date && (
            <p className="f2-card-date">{formatDate(item.date)}</p>
          )}
          <div className="f2-card-divider f2-card-lib-divider" role="separator" />
          <div className="f2-card-lib-foot">
            <div className="f2-stats">
              {item?.download_count != null && (
                <span className="f2-stat">
                  <Download size={14} />
                  {item.download_count}
                </span>
              )}
              {item?.view_count != null && (
                <span className="f2-stat">
                  <Eye size={14} />
                  {item.view_count}
                </span>
              )}
            </div>
            {(item?.experts || []).length > 0 && (
              <ExpertAvatars people={item.experts} showSingleName={false} />
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function getCountryCode(countryValue) {
  if (!countryValue) return null;
  const normalizedValue = countryValue.trim().toUpperCase();
  if (countriesFR.some((c) => c.code === normalizedValue)) {
    return normalizedValue;
  }
  const countryNameMap = {
    morocco: "Maroc",
    maroc: "Maroc",
    france: "France",
    algeria: "Algérie",
    tunisia: "Tunisie",
    spain: "Espagne",
    italy: "Italie",
    germany: "Allemagne",
    belgium: "Belgique",
    canada: "Canada",
    "united states": "États-Unis",
    usa: "États-Unis",
    "united kingdom": "Royaume-Uni",
    uk: "Royaume-Uni",
  };
  const mappedName = countryNameMap[countryValue.toLowerCase()];
  const searchName = mappedName || countryValue;
  const countryObj = countriesFR.find(
    (c) =>
      c.label?.toLowerCase() === searchName.toLowerCase() ||
      c.label?.toLowerCase().includes(searchName.toLowerCase()) ||
      searchName.toLowerCase().includes(c.label?.toLowerCase()),
  );
  return countryObj ? countryObj.code : null;
}

function expertDisplayName({ title, first_name, last_name, full_name }) {
  const prefix =
    title === "DOC" ? "Dr" : title === "PROF" ? "Pr" : "";
  const name =
    `${first_name || ""} ${last_name || ""}`.trim() || full_name || "";
  return `${prefix ? `${prefix} ` : ""}${name}`.trim();
}

function avisExcerpt(item) {
  return (
    item?.retenir ||
    item?.resume ||
    item?.description ||
    "Point de vue d'expert pour éclairer une décision."
  );
}

/** Compact aside card — mirrors ExpertV2Card on detail pages. */
export function AvisV2AsideCard({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category = item?.category?.name || item?.Categorie?.name || "Avis";
  const expert = item?.expert;
  const expertName = expert ? expertDisplayName(expert) : "Expert";
  const portrait = expert?.profile_image
    ? getMediaUrl(expert.profile_image) || expert.profile_image
    : expert
      ? expertImages[expert.gender === "M" ? 11 : 17]
      : null;
  const isFavorite = Boolean(
    item?.is_favorite ?? item?.IsFavorite ?? item?.is_bookmarked,
  );

  return (
    <motion.article
      {...cardMotion}
      className="f2-card ex-card av-aside-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <CardActionButtons
        id={item.id}
        favoriteType="expertopinion"
        editType="avis"
        initialIsFavorite={isFavorite}
        onFavoriteChange={onFavoriteChange}
        isOnFavoritesPage={isOnFavoritesPage}
        size="small"
        className="ex-edit"
      />

      <div
        className={`f2-card-media is-portrait${portrait ? "" : " is-empty"}`}
      >
        {portrait ? <img src={portrait} alt="" /> : null}
        <div className="f2-card-media-shade" />
        <div className="f2-card-media-top">
          <span className="v2-pill v2-avis-pill">
            <HighlightText text={category} query={highlightQuery} />
          </span>
        </div>
      </div>

      <div className="ex-card-body">
        <div className="ex-card-head">
          <h3 className="ex-card-title">
            <HighlightText text={item?.title} query={highlightQuery} />
          </h3>
        </div>

        <p className="ex-card-resume">
          <HighlightText text={avisExcerpt(item)} query={highlightQuery} />
        </p>

        <div className="ex-card-foot">
          <span className="ex-followers">{expertName}</span>
          <span className="av-aside-cta">Lire l&apos;avis</span>
        </div>
      </div>
    </motion.article>
  );
}

/** Expert listing card with follow. */
export function ExpertV2Card({ expert, onOpen, highlightQuery = "" }) {
  const [isFollowed, setIsFollowed] = useState(expert.is_followed);
  const [followerCount, setFollowerCount] = useState(expert.total_followers);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsFollowed(expert.is_followed);
    setFollowerCount(expert.total_followers);
  }, [expert.id, expert.is_followed, expert.total_followers]);

  const name = expertDisplayName(expert);
  const professionLabel =
    typeof expert.profession === "string"
      ? expert.profession
      : expert.profession?.name || "";
  const countryCode = getCountryCode(expert.country);
  const countryObj = countryCode
    ? countriesFR.find((c) => c.code === countryCode)
    : null;
  const portrait =
    expert.profile_image ||
    expertImages[expert.gender === "M" ? 11 : 17];

  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    const wasFollowed = isFollowed;
    try {
      setIsFollowed(!wasFollowed);
      setFollowerCount((prev) => (wasFollowed ? prev - 1 : prev + 1));
      await axios.post(`/api/experts/follow/${expert.id}/`);
      openSnackbar({
        open: true,
        message: wasFollowed
          ? `Vous ne suivez plus ${name}`
          : `Vous suivez maintenant ${name}`,
        variant: "alert",
        alert: { color: wasFollowed ? "error" : "success" },
        autoHideDuration: 4000,
      });
    } catch (error) {
      setIsFollowed(wasFollowed);
      setFollowerCount(expert.total_followers);
      console.error("Error toggling follow status:", error);
      openSnackbar({
        open: true,
        message: "Une erreur s'est produite lors de la modification du suivi",
        variant: "alert",
        alert: { color: "error" },
        autoHideDuration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card ex-card"
      onClick={() => onOpen?.(expert.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(expert.id);
      }}
    >
      <CardActionButtons
        id={expert.id}
        editType="expert"
        size="small"
        className="ex-edit"
      />

      <div
        className={`f2-card-media is-portrait${portrait ? "" : " is-empty"}`}
      >
        {portrait && <img src={portrait} alt="" />}
        <div className="f2-card-media-shade" />
        {professionLabel ? (
          <div className="f2-card-media-top">
            <span className="v2-pill v2-avis-pill">
              <HighlightText text={professionLabel} query={highlightQuery} />
            </span>
          </div>
        ) : null}
      </div>

      <div className="ex-card-body">
        <div className="ex-card-head">
          <h3 className="ex-card-title">
            <HighlightText text={name} query={highlightQuery} />
          </h3>
          {countryCode && (
            <img
              className="ex-card-flag"
              loading="lazy"
              src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
              alt={countryObj?.label || expert.country || ""}
              title={countryObj?.label || expert.country || ""}
            />
          )}
        </div>

        {expert.resume && (
          <p className="ex-card-resume">
            <HighlightText text={expert.resume} query={highlightQuery} />
          </p>
        )}

        <div className="ex-card-foot">
          <span className="ex-followers">
            {followerCount ?? 0} abonné
            {(followerCount ?? 0) !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            className={`ex-follow-btn${isFollowed ? " is-following" : " is-idle"}`}
            onClick={handleFollowToggle}
            disabled={isLoading}
          >
            {isLoading ? (
              "Chargement…"
            ) : isFollowed ? (
              <>
                <UserMinus2 size={16} />
                Abonné
              </>
            ) : (
              <>
                <UserPlus2 size={16} />
                Suivre
              </>
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

/** Institution listing card with follow. */
export function InstitutionV2Card({
  institution,
  onOpen,
  highlightQuery = "",
}) {
  const safeTotalFollowers =
    typeof institution.total_followers === "number" &&
    Number.isFinite(institution.total_followers)
      ? institution.total_followers
      : 0;
  const [isFollowed, setIsFollowed] = useState(
    Boolean(institution.is_followed),
  );
  const [followerCount, setFollowerCount] = useState(safeTotalFollowers);
  const [isLoading, setIsLoading] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [iconFailed, setIconFailed] = useState(false);

  useEffect(() => {
    setIsFollowed(Boolean(institution.is_followed));
    setFollowerCount(
      typeof institution.total_followers === "number" &&
        Number.isFinite(institution.total_followers)
        ? institution.total_followers
        : 0,
    );
    setLogoFailed(false);
    setIconFailed(false);
  }, [institution.id, institution.is_followed, institution.total_followers]);

  const logoSrc =
    !logoFailed && institution.logo ? getMediaUrl(institution.logo) : null;
  const iconSrc =
    !iconFailed && institution.icon ? getMediaUrl(institution.icon) : null;

  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    const wasFollowed = isFollowed;
    try {
      setIsFollowed(!wasFollowed);
      setFollowerCount((prev) => (wasFollowed ? prev - 1 : prev + 1));
      await axios.post(`/api/institutions/follow/${institution.id}/`);
      openSnackbar({
        open: true,
        message: wasFollowed
          ? `Vous ne suivez plus ${institution.name}`
          : `Vous suivez maintenant ${institution.name}`,
        variant: "alert",
        alert: { color: wasFollowed ? "error" : "success" },
        autoHideDuration: 4000,
      });
    } catch (error) {
      setIsFollowed(wasFollowed);
      setFollowerCount(safeTotalFollowers);
      console.error("Error toggling follow status:", error);
      openSnackbar({
        open: true,
        message: "Une erreur s'est produite lors de la modification du suivi",
        variant: "alert",
        alert: { color: "error" },
        autoHideDuration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card in-card"
      onClick={() => onOpen?.(institution.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(institution.id);
      }}
    >
      <CardActionButtons
        id={institution.id}
        editType="institution"
        size="small"
        className="in-edit"
      />

      <div
        className={`f2-card-media is-logo in-card-media${logoSrc ? " has-logo" : " is-empty"}`}
      >
        {logoSrc ? (
          <img
            className="in-card-media-logo"
            src={logoSrc}
            alt=""
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <img
            className="in-card-media-fallback"
            src={institutionEmptyIcon}
            alt=""
            style={{ filter: brand.iconFilters.accent }}
          />
        )}
        {institution.organization_type?.name ? (
          <div className="f2-card-media-top">
            <span className="v2-pill v2-avis-pill">
              <HighlightText
                text={institution.organization_type.name}
                query={highlightQuery}
              />
            </span>
          </div>
        ) : null}
      </div>

      <div className="in-card-body">
        <h3 className="in-card-title">
          {iconSrc ? (
            <img
              className="in-card-icon"
              src={iconSrc}
              alt=""
              onError={() => setIconFailed(true)}
            />
          ) : null}
          <span className="in-card-title-text">
            <HighlightText text={institution.name} query={highlightQuery} />
          </span>
        </h3>

        {institution.resume && (
          <p className="in-card-resume">
            <HighlightText text={institution.resume} query={highlightQuery} />
          </p>
        )}

        <div className="in-card-foot">
          <div className="in-card-meta">
            <span>
              <HighlightText
                text={institution.city || "—"}
                query={highlightQuery}
              />
            </span>
            <span>
              {followerCount} abonné{followerCount !== 1 ? "s" : ""}
            </span>
          </div>
          <button
            type="button"
            className={`in-follow-btn${isFollowed ? " is-following" : " is-idle"}`}
            onClick={handleFollowToggle}
            disabled={isLoading}
          >
            {isLoading ? (
              "Chargement…"
            ) : isFollowed ? (
              <>
                <UserMinus2 size={16} />
                Abonné
              </>
            ) : (
              <>
                <UserPlus2 size={16} />
                Suivre
              </>
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function helperName(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?.name || "";
}

function mediaSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return normalizeMediaUrl(value) || getMediaUrl(value) || null;
}

function inferFormeFromName(fullName) {
  const name = String(fullName || "").toLowerCase();
  if (name.includes("ml")) return "Solution buvable";
  if (name.includes("mg")) return "Comprimé";
  if (name.includes("mcg")) return "Gélule";
  if (/\bg\b/.test(name) || name.includes(" g ")) return "Poudre";
  return "";
}

function medicationTypeInfo(type) {
  if (!type) return null;
  const raw = String(type);
  if (raw.includes("Princeps") || raw === "PRINCEPS" || raw === "P") {
    return { letter: "P", label: "Princeps", tone: "princeps" };
  }
  if (raw.includes("Générique") || raw === "GENERIQUE" || raw === "G") {
    return { letter: "G", label: "Générique", tone: "generique" };
  }
  if (raw.includes("Biosimilaire") || raw === "BIOSIMILAIRE" || raw === "B") {
    return { letter: "B", label: "Biosimilaire", tone: "biosimilaire" };
  }
  return null;
}

function reimbursementPercent(info) {
  if (info == null) return 0;
  if (typeof info === "number") return info;
  if (info.percentage) return parseInt(info.percentage, 10) || 0;
  if (info.is_reimbursed) {
    return typeof info.is_reimbursed === "number" ? info.is_reimbursed : 100;
  }
  return 0;
}

function stockStatus(availability, tensionStock) {
  const value = availability || (typeof tensionStock === "string" ? tensionStock : null);
  if (value) {
    const lower = String(value).toLowerCase();
    if (lower.includes("disponible")) return { status: "disponible", label: "Disponible" };
    if (lower.includes("tension")) return { status: "tensions", label: "Tensions de stock" };
    if (lower.includes("rupture")) return { status: "rupture", label: "Rupture" };
  }
  if (tensionStock === true) return { status: "tensions", label: "Tensions de stock" };
  return null;
}

/** Medication listing card — full catalog info, V2 layout. */
export function MedicationV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const [formeIconFailed, setFormeIconFailed] = useState(false);
  const [cnssFailed, setCnssFailed] = useState(false);

  const title = item?.full_name || item?.name || "Médicament";
  const institution = item?.institution;
  const labName =
    typeof institution === "string" ? institution : institution?.name || "";
  const dci = helperName(item?.active_principle);
  const forme =
    helperName(item?.forme_galenique) || inferFormeFromName(title);
  const therapeutic = helperName(item?.therapeutic_class);
  const formeIcon = !formeIconFailed
    ? mediaSrc(item?.forme_galenique?.icon_url || item?.forme_galenique?.icon)
    : null;
  const packShot = !imgFailed ? mediaSrc(item?.image) : null;
  const typeInfo = medicationTypeInfo(item?.medication_type);
  const cnssPct = reimbursementPercent(item?.reimbursement_info);
  const stock = stockStatus(item?.availability, item?.tension_stock);
  const labInstitutions =
    typeof institution === "object" && institution
      ? [institution]
      : labName
        ? [{ name: labName }]
        : [];
  const formationIds = Array.isArray(item?.formation_ids) ? item.formation_ids : [];
  const isFavorite = Boolean(
    item?.is_favorite ?? item?.is_bookmarked ?? item?.IsFavorite ?? item?.isSaved,
  );
  const hasAlerts =
    (item?.related_news && item.related_news.length > 0) ||
    (item?.related_opinions && item.related_opinions.length > 0);
  const commercialized = Boolean(item?.is_commercialized);

  return (
    <motion.article
      {...cardMotion}
      className="f2-card med-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media is-logo${packShot ? " has-packshot" : " is-empty"}`}>
        {packShot ? (
          <img
            src={packShot}
            alt=""
            onError={() => setImgFailed(true)}
          />
        ) : (
          <EmptyMediaIcon src={medicationEmptyIcon} />
        )}

        <div className="f2-card-media-top med-card-badges">
          {typeInfo ? (
            <span
              className={`med-badge med-badge-type med-badge-type--${typeInfo.tone}`}
              data-tooltip={typeInfo.label}
              aria-label={typeInfo.label}
            >
              {typeInfo.letter}
            </span>
          ) : null}
          {item?.is_essential ? (
            <span className="med-badge med-badge-essential" title="Médicament essentiel">
              E
            </span>
          ) : null}
          {formationIds.length > 0 && (
            <span
              className="med-badge med-badge-formation"
              title={`Associé à ${formationIds.length} formation${formationIds.length > 1 ? "s" : ""}`}
            >
              <img src={formationBadgeIcon} alt="" />
              {formationIds.length > 1 ? (
                <em>{formationIds.length}</em>
              ) : null}
            </span>
          )}
          {cnssPct > 0 && (
            <span
              className="med-badge med-badge-cnss"
              title={`Remboursement CNSS ${cnssPct}%`}
              style={{ "--med-cnss": `${cnssPct * 3.6}deg` }}
            >
              <span className="med-cnss-ring" aria-hidden="true" />
              <span className="med-cnss-mark">
                {cnssFailed ? (
                  "C"
                ) : (
                  <img src={cnssLogo} alt="" onError={() => setCnssFailed(true)} />
                )}
              </span>
              <strong>{cnssPct}%</strong>
            </span>
          )}
          {hasAlerts ? (
            <span className="med-badge med-badge-alert" title="Contenu critique associé">
              <DangerIndicator size="medium" />
            </span>
          ) : null}
          <CardActionButtons
            id={item.id}
            favoriteType="medication"
            editType="medicament"
            initialIsFavorite={isOnFavoritesPage ? true : isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        {(dci || therapeutic) && (
          <div className="f2-card-media-bottom med-card-media-bottom">
            <div className="med-card-media-left">
              {dci ? (
                <span className="med-tag med-tag--dci med-card-media-chip">
                  <HighlightText text={dci} query={highlightQuery} />
                </span>
              ) : null}
              {therapeutic ? (
                <span className="med-tag med-tag--tx med-card-media-chip">
                  <HighlightText text={therapeutic} query={highlightQuery} />
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="med-card-body">
        <div className="med-card-heading">
          <h3 className="med-card-title">
            <HighlightText text={title} query={highlightQuery} />
          </h3>
        </div>

        {labInstitutions.length > 0 ? (
          <CardInstitutionRelations
            institutions={labInstitutions}
            highlightQuery={highlightQuery}
            ariaLabel="Laboratoire"
            max={1}
          />
        ) : null}

        <div className="med-card-divider" aria-hidden="true" />

        <div className="med-card-status-row">
          <div className="med-card-status-left">
            {forme ? (
              formeIcon ? (
                <span
                  className="med-card-forme-icon"
                  data-tooltip={forme}
                  aria-label={forme}
                >
                  <img
                    src={formeIcon}
                    alt=""
                    onError={() => setFormeIconFailed(true)}
                  />
                </span>
              ) : (
                <span className="med-tag med-tag--forme med-card-forme-chip">
                  <HighlightText text={forme} query={highlightQuery} />
                </span>
              )
            ) : null}
          </div>
          <div className="med-card-status-right">
            <span
              className={`md-patho-chip md-patho-chip--${commercialized ? "disponible" : "rupture"}`}
            >
              {commercialized ? "Commercialisé" : "Non commercialisé"}
            </span>
            {stock ? (
              <span className={`md-patho-chip md-patho-chip--${stock.status}`}>
                {stock.label}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

const SEVERITY_LABELS = {
  MILD: "Légère",
  MODERATE: "Modérée",
  SEVERE: "Sévère",
  CRITICAL: "Critique",
};

const TYPE_LABELS = {
  pediatrique: "Pédiatrique",
  geriatrie: "Gériatrie",
  adulte: "Adulte",
};

/** Pathology / disease listing card. */
export function PathologyV2Card({ item, onOpen, highlightQuery = "" }) {
  const category = item?.category_name || item?.category?.name || null;
  const severity = SEVERITY_LABELS[item?.severity] || item?.severity || null;
  const typeLabel = TYPE_LABELS[item?.type] || item?.type || null;
  const categoryColor = item?.category?.color || item?.color || "0049E0";
  const categoryIcon = mediaSrc(item?.category?.icon);
  const allInstitutions = Array.isArray(item?.research_institutions)
    ? item.research_institutions.filter(Boolean)
    : [];
  const people = Array.isArray(item?.experts) ? item.experts : [];

  return (
    <motion.article
      {...cardMotion}
      className="f2-card path-card"
      style={{ "--path-category": `#${String(categoryColor).replace("#", "")}` }}
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className="path-card-actions">
        <CardActionButtons
          id={item.id}
          favoriteType="disease"
          editType="disease"
          initialIsFavorite={Boolean(item?.is_favorite)}
          size="small"
          className="f2-card-actions"
        />
      </div>

      <span
        className={`path-card-category-mark${categoryIcon ? "" : " path-card-category-mark--fallback"}`}
        style={{
          WebkitMaskImage: `url(${categoryIcon || pathologyEmptyIcon})`,
          maskImage: `url(${categoryIcon || pathologyEmptyIcon})`,
        }}
        aria-hidden
      />

      <div className="path-card-body">
        <div className="path-card-main">
          {category && (
            <span className="path-card-chip">
              {categoryIcon ? (
                <span
                  className="path-card-chip-icon"
                  style={{
                    WebkitMaskImage: `url(${categoryIcon})`,
                    maskImage: `url(${categoryIcon})`,
                  }}
                  aria-hidden
                />
              ) : null}
              <HighlightText text={category} query={highlightQuery} />
            </span>
          )}
          <h3 className="path-card-title">
            <HighlightText text={item?.name || "Pathologie"} query={highlightQuery} />
          </h3>
          {item?.scientific_name && (
            <p className="path-card-sci">
              <HighlightText text={item.scientific_name} query={highlightQuery} />
            </p>
          )}
          {item?.description && (
            <p className="path-card-resume">
              <HighlightText text={item.description} query={highlightQuery} />
            </p>
          )}
          <div className="path-card-meta">
            {typeLabel ? <span>{typeLabel}</span> : null}
            {item?.icd_code ? <span>CIM {item.icd_code}</span> : null}
            {severity ? (
              <span className={`path-severity path-severity--${String(item.severity).toLowerCase()}`}>
                {severity}
              </span>
            ) : null}
          </div>
        </div>

        {(allInstitutions.length || people.length) ? (
          <>
            <div className="path-card-divider" role="separator" />
            <div className="path-card-relations">
              <CardInstitutionRelations
                institutions={allInstitutions}
                highlightQuery={highlightQuery}
                ariaLabel="Institutions associées"
                max={2}
              />
              {people.length > 0 ? (
                <ExpertAvatars people={people} showSingleName={false} />
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </motion.article>
  );
}
